import { useState, useEffect, useRef } from "react";
import { GlobalStyle, Container, Title, Button, List, ListItem, Input, Stats, StatCard, StatTitle, GuessedList, Timer, CompletionMessage, RegionHeader, TotalRemaining, CorrectCountiesSection, SectionHeading } from "./styles";
import { counties, findCounty } from "./data";

const App = () => {
  const [page, setPage] = useState<"home" | "game">("home");
  const [foundCounties, setFoundCounties] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('foundCounties');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [input, setInput] = useState("");
  const [guesses, setGuesses] = useState<string[]>([]);
  const [startTime, setStartTime] = useState<number | null>(() => {
    const saved = localStorage.getItem('startTime');
    return saved ? parseInt(saved) : null;
  });
  const [elapsedTime, setElapsedTime] = useState(0);
  const [inputError, setInputError] = useState(false);
  const [inputSuccess, setInputSuccess] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (startTime && foundCounties.size < counties.length) {
      interval = setInterval(() => {
        setElapsedTime(Date.now() - startTime);
      }, 100);
    }
    return () => clearInterval(interval);
  }, [startTime, foundCounties.size]);

  // Save progress to localStorage
  useEffect(() => {
    localStorage.setItem('foundCounties', JSON.stringify(Array.from(foundCounties)));
  }, [foundCounties]);

  useEffect(() => {
    if (startTime) {
      localStorage.setItem('startTime', startTime.toString());
    }
  }, [startTime]);

  const startGame = () => {
    setPage("game");
    if (!startTime) {
      const now = Date.now();
      setStartTime(now);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const county = findCounty(input);
    if (county && !foundCounties.has(county.name)) {
      setFoundCounties(prev => {
        const newSet = new Set(prev);
        newSet.add(county.name);
        return newSet;
      });
      setInputSuccess(true);
      setTimeout(() => setInputSuccess(false), 1000);
    } else {
      setInputError(true);
      setTimeout(() => setInputError(false), 1000);
    }
    
    setGuesses(prev => [...prev, input]);
    setInput("");
    
    // Keep focus on input and scroll into view
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  };

  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getStats = () => {
    const byCountry: Record<string, { found: number; total: number }> = {};
    const byLetter: Record<string, { found: number; total: number }> = {};

    counties.forEach(county => {
      const country = county.country;
      const letter = county.name[0];
      const isFound = foundCounties.has(county.name);

      if (!byCountry[country]) byCountry[country] = { found: 0, total: 0 };
      byCountry[country].total++;
      if (isFound) byCountry[country].found++;

      if (!byLetter[letter]) byLetter[letter] = { found: 0, total: 0 };
      byLetter[letter].total++;
      if (isFound) byLetter[letter].found++;
    });

    return { byCountry, byLetter };
  };

  const shareWhatsApp = () => {
    const message = `I completed the UK Counties game in ${formatTime(elapsedTime)}! Can you beat my time?`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`);
    
    // Clear saved progress after completion
    localStorage.removeItem('foundCounties');
    localStorage.removeItem('startTime');
  };

  const resetGame = () => {
    setFoundCounties(new Set());
    setStartTime(null);
    setElapsedTime(0);
    setGuesses([]);
    localStorage.removeItem('foundCounties');
    localStorage.removeItem('startTime');
  };

  const getRegionGroups = () => {
    const groups: Record<string, typeof counties> = {};
    counties.forEach(county => {
      const key = county.country;
      if (!groups[key]) groups[key] = [];
      groups[key].push(county);
    });
    return groups;
  };

  const sortedFoundCounties = counties
    .filter(county => foundCounties.has(county.name))
    .sort((a, b) => a.name.localeCompare(b.name));
  
  const stats = getStats();
  const isComplete = foundCounties.size === counties.length;
  const regionGroups = getRegionGroups();

  if (page === "home") {
    return (
      <>
        <GlobalStyle />
        <Container>
          <Title>UK Counties Game</Title>
          <List>
            {Object.entries(regionGroups).map(([country, countiesInCountry]) => (
              <div key={country}>
                <RegionHeader>
                  {country} <span>({countiesInCountry.length} counties)</span>
                </RegionHeader>
                {countiesInCountry.map(county => (
                  <ListItem key={county.name}>
                    <strong>{county.name}</strong> - {county.region} - {county.countyTown}
                  </ListItem>
                ))}
              </div>
            ))}
          </List>
          <div style={{ textAlign: 'center' }}>
            <Button onClick={startGame}>Test Yourself</Button>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <GlobalStyle />
      <Container>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <Button onClick={() => setPage("home")}>← Back</Button>
          <Timer>{formatTime(elapsedTime)}</Timer>
          <Button onClick={resetGame} style={{ background: 'rgba(255, 255, 255, 0.1)' }}>Reset</Button>
        </div>

        <Stats>
          {Object.entries(stats.byCountry).map(([country, stat]) => (
            <StatCard key={country}>
              <StatTitle>{country}</StatTitle>
              <div>{stat.found} of {stat.total}</div>
            </StatCard>
          ))}
        </Stats>

        <Stats>
          {Object.entries(stats.byLetter).map(([letter, stat]) => (
            <StatCard key={letter}>
              <StatTitle>{letter}</StatTitle>
              <div>{stat.found} of {stat.total}</div>
            </StatCard>
          ))}
        </Stats>

        {foundCounties.size > 0 && (
          <CorrectCountiesSection>
            <SectionHeading>Correct Counties</SectionHeading>
            <List>
              {sortedFoundCounties.map(county => (
                <ListItem key={county.name} found={true}>
                  {county.name}
                </ListItem>
              ))}
            </List>
            <TotalRemaining>
              {foundCounties.size} found, {counties.length - foundCounties.size} counties remaining
            </TotalRemaining>
          </CorrectCountiesSection>
        )}

        <GuessedList>
          {guesses.length > 0 && `Recent guesses: ${guesses.slice(-5).join(', ')}`}
          <div style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
            You can enter shorthand like 'Herts', 'Bucks', etc.
          </div>
        </GuessedList>

        {isComplete ? (
          <CompletionMessage>
            <h2>Congratulations!</h2>
            <p>You completed all {counties.length} counties in {formatTime(elapsedTime)}!</p>
            <Button onClick={shareWhatsApp} style={{ marginTop: '1rem' }}>
              Share on WhatsApp
            </Button>
          </CompletionMessage>
        ) : (
          <form onSubmit={handleSubmit}>
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Enter a UK county..."
              autoFocus
              error={inputError}
              success={inputSuccess}
            />
            <Button type="submit">Submit</Button>
          </form>
        )}
      </Container>
    </>
  );
};

export default App;
